import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import {
  Users, FileUp, BookOpen, Loader2, Plus, Trash2, Pencil,
  ClipboardCheck, ListTodo, FileText, PieChart, Download, FileSpreadsheet,
  CheckCircle2, XCircle, AlertCircle, Building2, Send, Copy, Mail, Route
} from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import AnalyticsTab from './admin/AnalyticsTab';
import UploadTab from './admin/UploadTab';
import TeamTab from './admin/TeamTab';
import PathsTab from './admin/PathsTab';
import DepartmentTab from './admin/DepartmentTab';

const fadeUp = { initial: { opacity: 0, y: 10 }, animate: { opacity: 1, y: 0 } };

export default function AdminDashboard() {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState({});
  const [users, setUsers] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [modules, setModules] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [showAddUser, setShowAddUser] = useState(false);
  const [newUser, setNewUser] = useState({ name: '', email: '', password: '', role: 'staff', location: '', department: '', position: '' });
  const [showAssign, setShowAssign] = useState(false);
  const [assignModuleId, setAssignModuleId] = useState('');
  const [assignUserIds, setAssignUserIds] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [csvUploading, setCsvUploading] = useState(false);
  const [csvResult, setCsvResult] = useState(null);
  const [showCsvResult, setShowCsvResult] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showEditUser, setShowEditUser] = useState(false);
  const [positions, setPositions] = useState([]);
  const [showInvite, setShowInvite] = useState(false);
  const [invite, setInvite] = useState({ email: '', role: 'staff', department: '', position: '', location: '' });
  const [inviteSending, setInviteSending] = useState(false);
  const [lastInviteLink, setLastInviteLink] = useState('');

  const loadPositions = useCallback(async (dept) => {
    if (!dept || (dept !== 'FOH' && dept !== 'BOH')) {
      setPositions([]);
      return;
    }
    try {
      const { data } = await api('get', `/positions?department=${dept}`);
      setPositions(data);
    } catch {
      setPositions([]);
    }
  }, [api]);

  const loadData = useCallback(async () => {
    try {
      const [statsRes, usersRes, docsRes, modulesRes] = await Promise.all([
        api('get', '/dashboard/stats'),
        api('get', '/users'),
        api('get', '/documents'),
        api('get', '/training-modules')
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data);
      setDocuments(docsRes.data);
      setModules(modulesRes.data);
    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.error('Failed to load admin data:', err);
    }
  }, [api]);

  const loadAnalytics = useCallback(async () => {
    try {
      const { data } = await api('get', '/analytics/overview');
      setAnalytics(data);
    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.error('Failed to load analytics:', err);
    }
  }, [api]);

  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => { if (activeTab === 'analytics') loadAnalytics(); }, [activeTab, loadAnalytics]);

  const handleFileUpload = async (e, opts = {}) => {
    const { category = 'general', position = '', targetAudience = 'both', location = '', moduleType = 'single', pathId = '', questionFormats = '' } = opts;
    const file = e.target.files?.[0];
    if (!file) return;
    const ext = file.name.split('.').pop().toLowerCase();
    if (!['pdf', 'docx', 'doc'].includes(ext)) { toast.error('Only PDF and Word documents are accepted'); return; }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('category', category);
      if (position) formData.append('position', position);
      formData.append('target_audience', targetAudience);
      if (location) formData.append('location', location);
      formData.append('module_type', moduleType);
      if (pathId) formData.append('path_id', pathId);
      if (questionFormats) formData.append('question_formats', questionFormats);
      const { data } = await api('post', '/documents/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(data.message || 'Document uploaded — AI is generating training content.');
      // Poll for completion
      if (data.document_id) {
        const pollDocStatus = async () => {
          for (let i = 0; i < 60; i++) {
            await new Promise(r => setTimeout(r, 5000));
            try {
              const { data: docs } = await api('get', '/documents');
              const doc = docs.find(d => d.id === data.document_id);
              if (doc?.status === 'review') {
                toast.success(`Draft pathway ready: ${doc.module_count} modules generated. Review and publish it.`, {
                  action: { label: 'Review', onClick: () => navigate(`/admin/pathway/${doc.id}`) }, duration: 10000,
                });
                loadData();
                setUploading(false);
                return;
              }
              if (doc?.status === 'completed') {
                toast.success('Training module created successfully!');
                loadData();
                setUploading(false);
                return;
              }
              if (doc?.status === 'failed') {
                toast.error('AI processing failed. Please try again.');
                loadData();
                setUploading(false);
                return;
              }
            } catch { /* continue polling */ }
          }
          toast.info('Still processing... Refresh the page to check status.');
          setUploading(false);
          loadData();
        };
        pollDocStatus();
      } else {
        loadData();
        setUploading(false);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
      setUploading(false);
    }
    finally { e.target.value = ''; }
  };

  const deleteDocument = async (docId) => {
    if (!window.confirm('Delete this document? Training modules and tests created from it will be preserved.')) return;
    try {
      await api('delete', `/documents/${docId}`);
      toast.success('Document deleted');
      loadData();
    } catch { toast.error('Failed to delete document'); }
  };

  const addUser = async () => {
    try {
      await api('post', '/auth/register', newUser);
      toast.success('User created successfully');
      setShowAddUser(false);
      setNewUser({ name: '', email: '', password: '', role: 'staff', location: '', department: '', position: '' });
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create user'); }
  };

  const sendInvite = async () => {
    if (!invite.email.trim()) { toast.error('Email is required'); return; }
    setInviteSending(true);
    try {
      const { data } = await api('post', '/invites', invite);
      toast.success(data.message);
      setLastInviteLink(data.invite_link);
      if (!data.email_sent) {
        toast.info('Email service unavailable. Share the invite link manually.');
      }
      setInvite({ email: '', role: 'staff', department: '', position: '', location: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send invite');
    } finally { setInviteSending(false); }
  };

  const deleteUser = async (userId) => {
    if (!window.confirm('Delete this user?')) return;
    try { await api('delete', `/users/${userId}`); toast.success('User deleted'); loadData(); }
    catch { toast.error('Failed to delete user'); }
  };

  const saveEditUser = async () => {
    if (!editingUser) return;
    try {
      const payload = {
        name: editingUser.name,
        email: editingUser.email,
        role: editingUser.role,
        location: editingUser.location,
        department: editingUser.department,
        position: editingUser.position || '',
      };
      if (editingUser.newPassword) payload.password = editingUser.newPassword;
      await api('put', `/users/${editingUser.id}`, payload);
      toast.success('User updated');
      setShowEditUser(false);
      setEditingUser(null);
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update user'); }
  };

  const openEditUser = (u) => {
    setEditingUser({ ...u, newPassword: '' });
    setShowEditUser(true);
    loadPositions(u.department);
  };

  const assignTraining = async () => {
    if (!assignModuleId || assignUserIds.length === 0) return;
    try {
      await api('post', `/training-modules/${assignModuleId}/assign`, { user_ids: assignUserIds });
      toast.success('Training assigned successfully');
      setShowAssign(false); setAssignUserIds([]); loadData();
    } catch { toast.error('Failed to assign training'); }
  };

  const toggleAssignUser = (uid) => {
    setAssignUserIds(prev => prev.includes(uid) ? prev.filter(id => id !== uid) : [...prev, uid]);
  };

  const deleteModule = async (moduleId) => {
    if (!window.confirm('Delete this module and its tests/tasks?')) return;
    try { await api('delete', `/training-modules/${moduleId}`); toast.success('Module deleted'); loadData(); }
    catch { toast.error('Failed to delete module'); }
  };

  const downloadCsvTemplate = async () => {
    try {
      const res = await api('get', '/users/csv-template', null, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url; a.download = 'grillsmith_users_template.csv';
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch { toast.error('Failed to download template'); }
  };

  const handleCsvUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.endsWith('.csv')) { toast.error('Please upload a CSV file'); return; }
    setCsvUploading(true); setCsvResult(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await api('post', '/users/bulk-import', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setCsvResult(data); setShowCsvResult(true);
      toast.success(data.message); loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'CSV import failed'); }
    finally { setCsvUploading(false); e.target.value = ''; }
  };

  const isAdmin = user?.role === 'admin';

  const statCards = [
    { icon: Users, label: 'Team Members', value: stats.total_users || 0 },
    { icon: BookOpen, label: 'Modules', value: stats.total_modules || 0 },
    { icon: ClipboardCheck, label: 'Tests', value: stats.total_tests || 0 },
    { icon: ListTodo, label: 'Tasks', value: stats.total_tasks || 0 },
    { icon: FileText, label: 'Documents', value: stats.total_documents || 0 }
  ];

  return (
    <div className="pb-6">
      <motion.div {...fadeUp} className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-stone-900" style={{ fontFamily: "'Outfit', sans-serif" }}>
          {isAdmin ? 'Admin' : 'Manager'} Dashboard
        </h1>
        <p className="text-stone-500 mt-1 text-sm" style={{ fontFamily: "'Manrope', sans-serif" }}>
          Manage training, track progress, upload documents
        </p>
      </motion.div>

      <motion.div {...fadeUp} transition={{ delay: 0.1 }} className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        {statCards.map((stat) => (
          <Card key={stat.label} className="bg-white rounded-2xl border border-stone-200 shadow-sm" data-testid={`admin-stat-${stat.label.toLowerCase().replace(/\s/g, '-')}`}>
            <CardContent className="p-4">
              <stat.icon className="w-5 h-5 text-[#D9480F] mb-2" />
              <p className="text-2xl font-bold text-stone-900">{stat.value}</p>
              <p className="text-xs text-stone-500">{stat.label}</p>
            </CardContent>
          </Card>
        ))}
      </motion.div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="w-full bg-stone-100 rounded-xl p-1 mb-4 flex flex-wrap" data-testid="admin-tabs">
          <TabsTrigger value="overview" className="flex-1 rounded-lg text-xs sm:text-sm data-[state=active]:bg-white data-[state=active]:text-[#1B2A4A]" data-testid="tab-team">
            <Users className="w-4 h-4 mr-1 hidden sm:inline" /> Team
          </TabsTrigger>
          <TabsTrigger value="paths" className="flex-1 rounded-lg text-xs sm:text-sm data-[state=active]:bg-white data-[state=active]:text-[#1B2A4A]" data-testid="tab-paths">
            <Route className="w-4 h-4 mr-1 hidden sm:inline" /> Paths
          </TabsTrigger>
          <TabsTrigger value="departments" className="flex-1 rounded-lg text-xs sm:text-sm data-[state=active]:bg-white data-[state=active]:text-[#1B2A4A]">
            <Building2 className="w-4 h-4 mr-1 hidden sm:inline" /> Depts
          </TabsTrigger>
          <TabsTrigger value="analytics" className="flex-1 rounded-lg text-xs sm:text-sm data-[state=active]:bg-white data-[state=active]:text-[#1B2A4A]">
            <PieChart className="w-4 h-4 mr-1 hidden sm:inline" /> Analytics
          </TabsTrigger>
          <TabsTrigger value="upload" className="flex-1 rounded-lg text-xs sm:text-sm data-[state=active]:bg-white data-[state=active]:text-[#1B2A4A]">
            <FileUp className="w-4 h-4 mr-1 hidden sm:inline" /> Upload
          </TabsTrigger>
          <TabsTrigger value="modules" className="flex-1 rounded-lg text-xs sm:text-sm data-[state=active]:bg-white data-[state=active]:text-[#1B2A4A]">
            <BookOpen className="w-4 h-4 mr-1 hidden sm:inline" /> Modules
          </TabsTrigger>
          <TabsTrigger value="users" className="flex-1 rounded-lg text-xs sm:text-sm data-[state=active]:bg-white data-[state=active]:text-[#1B2A4A]">
            <Users className="w-4 h-4 mr-1 hidden sm:inline" /> Users
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview"><TeamTab /></TabsContent>
        <TabsContent value="paths"><PathsTab modules={modules} isAdmin={isAdmin} /></TabsContent>
        <TabsContent value="departments"><DepartmentTab /></TabsContent>
        <TabsContent value="analytics"><AnalyticsTab analytics={analytics} /></TabsContent>
        <TabsContent value="upload"><UploadTab documents={documents} uploading={uploading} handleFileUpload={handleFileUpload} onDeleteDocument={deleteDocument} isAdmin={isAdmin} /></TabsContent>

        {/* Modules Tab */}
        <TabsContent value="modules">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-stone-900" style={{ fontFamily: "'Outfit', sans-serif" }}>Training Modules</h2>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => navigate('/admin/create-test')} className="rounded-xl text-sm border-stone-200" data-testid="create-test-btn">
                <Plus className="w-4 h-4 mr-1" /> Create Test
              </Button>
              <Dialog open={showAssign} onOpenChange={setShowAssign}>
              <DialogTrigger asChild>
                <Button className="bg-[#1B2A4A] text-white rounded-xl hover:bg-[#243658] text-sm" data-testid="assign-training-btn">Assign Training</Button>
              </DialogTrigger>
              <DialogContent className="rounded-2xl" data-testid="assign-dialog">
                <DialogHeader><DialogTitle style={{ fontFamily: "'Outfit', sans-serif" }}>Assign Training Module</DialogTitle></DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label>Select Module</Label>
                    <Select value={assignModuleId} onValueChange={setAssignModuleId}>
                      <SelectTrigger className="rounded-xl" data-testid="assign-module-select"><SelectValue placeholder="Choose a module" /></SelectTrigger>
                      <SelectContent>{modules.map(m => <SelectItem key={m.id} value={m.id}>{m.title}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Select Team Members</Label>
                    <div className="max-h-48 overflow-y-auto space-y-2 mt-2">
                      {users.filter(u => u.role !== 'admin').map(u => (
                        <label key={u.id} className="flex items-center gap-2 p-2 rounded-lg hover:bg-stone-50 cursor-pointer">
                          <input type="checkbox" checked={assignUserIds.includes(u.id)} onChange={() => toggleAssignUser(u.id)} className="rounded accent-[#1B2A4A]" />
                          <span className="text-sm text-stone-900">{u.name}</span>
                          <Badge className="bg-stone-100 text-stone-600 text-xs ml-auto">{u.role}</Badge>
                        </label>
                      ))}
                    </div>
                  </div>
                  <Button onClick={assignTraining} className="w-full bg-[#1B2A4A] text-white rounded-xl hover:bg-[#243658]" data-testid="confirm-assign-btn">
                    Assign to {assignUserIds.length} member{assignUserIds.length !== 1 ? 's' : ''}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
            </div>
          </div>
          <div className="space-y-3" data-testid="admin-modules-list">
            {modules.map(mod => (
              <Card key={mod.id} className="bg-white rounded-2xl border border-stone-200 shadow-sm" data-testid={`admin-module-${mod.id}`}>
                <CardContent className="p-4 sm:p-5">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="font-semibold text-stone-900" style={{ fontFamily: "'Outfit', sans-serif" }}>{mod.title}</h3>
                      <p className="text-sm text-stone-500 mt-1">{mod.description}</p>
                      <div className="flex items-center gap-3 mt-2 text-xs text-stone-400">
                        <span>{mod.module_type === 'modular' ? `${mod.lessons?.length || 0} sections` : `${mod.lessons?.length || 0} lessons`}</span>
                        <span>Created by {mod.created_by_name}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0 ml-3 flex-wrap justify-end">
                      {mod.module_type === 'modular' && <Badge className="bg-blue-100 text-blue-700 text-[10px]">Modular</Badge>}
                      <Badge className="bg-[#1B2A4A]/10 text-[#1B2A4A]">{mod.assigned_to?.length || 0} assigned</Badge>
                      {mod.category && mod.category !== 'general' && (
                        <Badge className={mod.category === 'FOH' ? 'bg-blue-100 text-blue-700' : 'bg-orange-100 text-orange-700'}>{mod.category}</Badge>
                      )}
                      {(!mod.category || mod.category === 'general') && (
                        <Badge className="bg-stone-100 text-stone-600">All</Badge>
                      )}
                      {mod.position && <Badge className="bg-violet-100 text-violet-700 text-[10px]">{mod.position}</Badge>}
                      {mod.status === 'draft' && <Badge className="bg-amber-100 text-amber-700 text-[10px]" data-testid={`draft-badge-${mod.id}`}>Draft</Badge>}
                      {mod.day_label && <Badge className="bg-[#D9480F]/10 text-[#D9480F] text-[10px]">{mod.day_label}</Badge>}
                      {mod.target_audience && mod.target_audience !== 'both' && (
                        <Badge className={mod.target_audience === 'manager' ? 'bg-amber-100 text-amber-700 text-[10px]' : 'bg-sky-100 text-sky-700 text-[10px]'}>
                          {mod.target_audience === 'manager' ? 'Mgr' : 'Staff'}
                        </Badge>
                      )}
                      {mod.location && <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">{mod.location}</Badge>}
                      <Button variant="ghost" size="sm" onClick={() => navigate(`/admin/module/${mod.id}`)}
                        className="text-stone-500 hover:text-[#1B2A4A]" data-testid={`edit-module-${mod.id}`}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      {isAdmin && (
                        <Button variant="ghost" size="sm" onClick={() => deleteModule(mod.id)}
                          className="text-red-400 hover:text-red-600" data-testid={`delete-module-${mod.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Users Tab */}
        <TabsContent value="users">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <h2 className="text-lg font-semibold text-stone-900" style={{ fontFamily: "'Outfit', sans-serif" }}>Team Members</h2>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={downloadCsvTemplate} className="rounded-xl text-sm border-stone-200" data-testid="download-csv-template">
                <Download className="w-4 h-4 mr-1" /> Template
              </Button>
              <label className="cursor-pointer">
                <input type="file" accept=".csv" onChange={handleCsvUpload} className="hidden" disabled={csvUploading} data-testid="csv-upload-input" />
                <Button asChild variant="outline" disabled={csvUploading} className="rounded-xl text-sm border-stone-200" data-testid="csv-import-btn">
                  <span>{csvUploading ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Importing...</> : <><FileSpreadsheet className="w-4 h-4 mr-1" /> Import CSV</>}</span>
                </Button>
              </label>
              <Dialog open={showAddUser} onOpenChange={setShowAddUser}>
                <DialogTrigger asChild>
                  <Button className="bg-[#1B2A4A] text-white rounded-xl hover:bg-[#243658] text-sm" data-testid="add-user-btn"><Plus className="w-4 h-4 mr-1" /> Add User</Button>
                </DialogTrigger>
                <DialogContent className="rounded-2xl" data-testid="add-user-dialog">
                  <DialogHeader><DialogTitle style={{ fontFamily: "'Outfit', sans-serif" }}>Add Team Member</DialogTitle></DialogHeader>
                  <div className="space-y-4">
                    <div><Label>Name</Label><Input value={newUser.name} onChange={e => setNewUser({...newUser, name: e.target.value})} className="rounded-xl" placeholder="Full name" data-testid="new-user-name" /></div>
                    <div><Label>Email</Label><Input type="email" value={newUser.email} onChange={e => setNewUser({...newUser, email: e.target.value})} className="rounded-xl" placeholder="email@grillsmith.com" data-testid="new-user-email" /></div>
                    <div><Label>Password</Label><Input type="password" value={newUser.password} onChange={e => setNewUser({...newUser, password: e.target.value})} className="rounded-xl" placeholder="Temporary password" data-testid="new-user-password" /></div>
                    <div className="grid grid-cols-2 gap-3">
                      <div><Label>Role</Label>
                        <Select value={newUser.role} onValueChange={v => setNewUser({...newUser, role: v})}>
                          <SelectTrigger className="rounded-xl" data-testid="new-user-role"><SelectValue /></SelectTrigger>
                          <SelectContent><SelectItem value="staff">Staff</SelectItem><SelectItem value="manager">Manager</SelectItem>{isAdmin && <SelectItem value="admin">Admin</SelectItem>}</SelectContent>
                        </Select>
                      </div>
                      <div><Label>Department</Label>
                        <Select value={newUser.department} onValueChange={v => { setNewUser({...newUser, department: v, position: ''}); loadPositions(v); }}>
                          <SelectTrigger className="rounded-xl" data-testid="new-user-department"><SelectValue placeholder="Select" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="FOH">FOH</SelectItem>
                            <SelectItem value="BOH">BOH</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    {(newUser.department === 'FOH' || newUser.department === 'BOH') && (
                      <div>
                        <Label>Position</Label>
                          <Select value={newUser.position} onValueChange={v => setNewUser({...newUser, position: v})}>
                            <SelectTrigger className="rounded-xl" data-testid="new-user-position"><SelectValue placeholder="Select position" /></SelectTrigger>
                            <SelectContent>
                              {positions.map(p => <SelectItem key={p.id} value={p.name}>{p.name}</SelectItem>)}
                            </SelectContent>
                          </Select>
                      </div>
                    )}
                    <div><Label>Location</Label>
                      <Select value={newUser.location} onValueChange={v => setNewUser({...newUser, location: v})}>
                        <SelectTrigger className="rounded-xl" data-testid="new-user-location"><SelectValue placeholder="Select location" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Carrollwood">Carrollwood</SelectItem><SelectItem value="Countryside">Countryside</SelectItem>
                          <SelectItem value="Lakeland">Lakeland</SelectItem><SelectItem value="Sarasota">Sarasota</SelectItem><SelectItem value="Wesley Chapel">Wesley Chapel</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <Button onClick={addUser} className="w-full bg-[#1B2A4A] text-white rounded-xl hover:bg-[#243658]" data-testid="confirm-add-user-btn">Create User</Button>
                  </div>
                </DialogContent>
              </Dialog>
              <Dialog open={showInvite} onOpenChange={(v) => { setShowInvite(v); if (!v) setLastInviteLink(''); }}>
                <DialogTrigger asChild>
                  <Button variant="outline" className="rounded-xl text-sm border-[#1B2A4A] text-[#1B2A4A] hover:bg-[#1B2A4A]/5" data-testid="invite-staff-btn">
                    <Mail className="w-4 h-4 mr-1" /> Invite Staff
                  </Button>
                </DialogTrigger>
                <DialogContent className="rounded-2xl" data-testid="invite-dialog">
                  <DialogHeader><DialogTitle style={{ fontFamily: "'Outfit', sans-serif" }}>Invite Team Member</DialogTitle></DialogHeader>
                  <p className="text-sm text-stone-500 -mt-1">Send an invite link via email. They'll set their own name and password.</p>
                  <div className="space-y-4 mt-2">
                    <div><Label>Email Address</Label><Input type="email" value={invite.email} onChange={e => setInvite({...invite, email: e.target.value})} className="rounded-xl" placeholder="email@grillsmith.com" data-testid="invite-email" /></div>
                    <div className="grid grid-cols-2 gap-3">
                      <div><Label>Role</Label>
                        <Select value={invite.role} onValueChange={v => setInvite({...invite, role: v})}>
                          <SelectTrigger className="rounded-xl" data-testid="invite-role"><SelectValue /></SelectTrigger>
                          <SelectContent><SelectItem value="staff">Staff</SelectItem><SelectItem value="manager">Manager</SelectItem>{isAdmin && <SelectItem value="admin">Admin</SelectItem>}</SelectContent>
                        </Select>
                      </div>
                      <div><Label>Department</Label>
                        <Select value={invite.department || 'none'} onValueChange={v => { setInvite({...invite, department: v === 'none' ? '' : v, position: ''}); loadPositions(v === 'none' ? '' : v); }}>
                          <SelectTrigger className="rounded-xl" data-testid="invite-department"><SelectValue /></SelectTrigger>
                          <SelectContent><SelectItem value="none">None</SelectItem><SelectItem value="FOH">FOH</SelectItem><SelectItem value="BOH">BOH</SelectItem></SelectContent>
                        </Select>
                      </div>
                    </div>
                    {(invite.department === 'FOH' || invite.department === 'BOH') && (
                      <div><Label>Position</Label>
                        <Select value={invite.position || 'none'} onValueChange={v => setInvite({...invite, position: v === 'none' ? '' : v})}>
                          <SelectTrigger className="rounded-xl" data-testid="invite-position"><SelectValue /></SelectTrigger>
                          <SelectContent><SelectItem value="none">Not set</SelectItem>{positions.map(p => <SelectItem key={p.id} value={p.name}>{p.name}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                    )}
                    <div><Label>Location</Label>
                      <Select value={invite.location || 'none'} onValueChange={v => setInvite({...invite, location: v === 'none' ? '' : v})}>
                        <SelectTrigger className="rounded-xl" data-testid="invite-location"><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="none">Not set</SelectItem>
                          <SelectItem value="Carrollwood">Carrollwood</SelectItem><SelectItem value="Countryside">Countryside</SelectItem>
                          <SelectItem value="Lakeland">Lakeland</SelectItem><SelectItem value="Sarasota">Sarasota</SelectItem><SelectItem value="Wesley Chapel">Wesley Chapel</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <Button onClick={sendInvite} disabled={inviteSending} className="w-full bg-[#1B2A4A] text-white rounded-xl hover:bg-[#243658]" data-testid="send-invite-btn">
                      {inviteSending ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending...</> : <><Send className="w-4 h-4 mr-2" /> Send Invite</>}
                    </Button>
                    {lastInviteLink && (
                      <div className="bg-stone-50 border border-stone-200 rounded-xl p-3">
                        <p className="text-xs text-stone-500 mb-1 font-medium">Invite link (share manually if email wasn't sent):</p>
                        <div className="flex items-center gap-2">
                          <Input value={lastInviteLink} readOnly className="rounded-lg text-xs bg-white flex-1" data-testid="invite-link-display" />
                          <Button variant="outline" size="sm" className="rounded-lg shrink-0" data-testid="copy-invite-link"
                            onClick={() => { navigator.clipboard.writeText(lastInviteLink); toast.success('Link copied!'); }}>
                            <Copy className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </div>

          {/* Edit User Dialog */}
          <Dialog open={showEditUser} onOpenChange={setShowEditUser}>
            <DialogContent className="rounded-2xl" data-testid="edit-user-dialog">
              <DialogHeader><DialogTitle style={{ fontFamily: "'Outfit', sans-serif" }}>Edit Team Member</DialogTitle></DialogHeader>
              {editingUser && (
                <div className="space-y-4">
                  <div><Label>Name</Label><Input value={editingUser.name} onChange={e => setEditingUser({...editingUser, name: e.target.value})} className="rounded-xl" data-testid="edit-user-name" /></div>
                  <div><Label>Email</Label><Input type="email" value={editingUser.email} onChange={e => setEditingUser({...editingUser, email: e.target.value})} className="rounded-xl" data-testid="edit-user-email" /></div>
                  <div><Label>New Password <span className="text-stone-400 font-normal">(leave blank to keep current)</span></Label>
                    <Input type="password" value={editingUser.newPassword} onChange={e => setEditingUser({...editingUser, newPassword: e.target.value})} className="rounded-xl" placeholder="New password" data-testid="edit-user-password" /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Role</Label>
                      <Select value={editingUser.role} onValueChange={v => setEditingUser({...editingUser, role: v})}>
                        <SelectTrigger className="rounded-xl" data-testid="edit-user-role"><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="staff">Staff</SelectItem><SelectItem value="manager">Manager</SelectItem>{isAdmin && <SelectItem value="admin">Admin</SelectItem>}</SelectContent>
                      </Select>
                    </div>
                    <div><Label>Department</Label>
                      <Select value={editingUser.department || ''} onValueChange={v => { setEditingUser({...editingUser, department: v, position: ''}); loadPositions(v); }}>
                        <SelectTrigger className="rounded-xl" data-testid="edit-user-department"><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="FOH">FOH</SelectItem>
                          <SelectItem value="BOH">BOH</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  {(editingUser.department === 'FOH' || editingUser.department === 'BOH') && (
                    <div>
                      <Label>Position</Label>
                        <Select value={editingUser.position || ''} onValueChange={v => setEditingUser({...editingUser, position: v})}>
                          <SelectTrigger className="rounded-xl" data-testid="edit-user-position"><SelectValue placeholder="Select position" /></SelectTrigger>
                          <SelectContent>
                            {positions.map(p => <SelectItem key={p.id} value={p.name}>{p.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                    </div>
                  )}
                  <div><Label>Location</Label>
                    <Select value={editingUser.location || ''} onValueChange={v => setEditingUser({...editingUser, location: v})}>
                      <SelectTrigger className="rounded-xl" data-testid="edit-user-location"><SelectValue placeholder="Select location" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Carrollwood">Carrollwood</SelectItem><SelectItem value="Countryside">Countryside</SelectItem>
                        <SelectItem value="Lakeland">Lakeland</SelectItem><SelectItem value="Sarasota">Sarasota</SelectItem><SelectItem value="Wesley Chapel">Wesley Chapel</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button onClick={saveEditUser} className="w-full bg-[#1B2A4A] text-white rounded-xl hover:bg-[#243658]" data-testid="save-edit-user-btn">Save Changes</Button>
                </div>
              )}
            </DialogContent>
          </Dialog>

          <Card className="bg-white rounded-2xl border border-stone-200 shadow-sm overflow-hidden" data-testid="users-table">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader><TableRow className="bg-stone-50">
                  <TableHead className="text-stone-600 font-semibold">Name</TableHead>
                  <TableHead className="text-stone-600 font-semibold">Email</TableHead>
                  <TableHead className="text-stone-600 font-semibold">Role</TableHead>
                  <TableHead className="text-stone-600 font-semibold">Dept</TableHead>
                  <TableHead className="text-stone-600 font-semibold">Position</TableHead>
                  <TableHead className="text-stone-600 font-semibold">Location</TableHead>
                  <TableHead className="text-stone-600 font-semibold">Actions</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {users.map(u => (
                    <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                      <TableCell className="font-medium text-stone-900">{u.name}</TableCell>
                      <TableCell className="text-stone-600">{u.email}</TableCell>
                      <TableCell><Badge className={u.role === 'admin' ? 'bg-[#1B2A4A]/10 text-[#1B2A4A]' : u.role === 'manager' ? 'bg-amber-100 text-amber-700' : 'bg-stone-100 text-stone-600'}>{u.role}</Badge></TableCell>
                      <TableCell>{u.department ? <Badge className={u.department === 'FOH' ? 'bg-blue-100 text-blue-700' : 'bg-orange-100 text-orange-700'}>{u.department}</Badge> : <span className="text-stone-400">-</span>}</TableCell>
                      <TableCell>
                        {u.position ? (
                          <Badge className="bg-violet-100 text-violet-700 text-[10px]">{u.position}</Badge>
                        ) : <span className="text-stone-400">-</span>}
                      </TableCell>
                      <TableCell className="text-stone-600">{u.location || '-'}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          {u.role !== 'admin' && (
                            <Button variant="ghost" size="sm" onClick={() => openEditUser(u)} className="text-stone-500 hover:text-[#1B2A4A]" data-testid={`edit-user-${u.id}`}>
                              <Pencil className="w-4 h-4" />
                            </Button>
                          )}
                          {isAdmin && u.role !== 'admin' && (
                            <Button variant="ghost" size="sm" onClick={() => deleteUser(u.id)} className="text-red-500 hover:text-red-700 hover:bg-red-50" data-testid={`delete-user-${u.id}`}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </Card>

          {/* CSV Import Result */}
          <Dialog open={showCsvResult} onOpenChange={setShowCsvResult}>
            <DialogContent className="rounded-2xl max-w-lg" data-testid="csv-result-dialog">
              <DialogHeader><DialogTitle style={{ fontFamily: "'Outfit', sans-serif" }}>Import Results</DialogTitle></DialogHeader>
              {csvResult && (
                <div className="space-y-3">
                  <p className="text-sm text-stone-600">{csvResult.message}</p>
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div className="p-3 rounded-xl bg-green-50 border border-green-100"><CheckCircle2 className="w-5 h-5 text-green-600 mx-auto mb-1" /><p className="text-lg font-bold text-green-700">{csvResult.created_count}</p><p className="text-xs text-green-600">Created</p></div>
                    <div className="p-3 rounded-xl bg-amber-50 border border-amber-100"><AlertCircle className="w-5 h-5 text-amber-600 mx-auto mb-1" /><p className="text-lg font-bold text-amber-700">{csvResult.skipped_count}</p><p className="text-xs text-amber-600">Skipped</p></div>
                    <div className="p-3 rounded-xl bg-red-50 border border-red-100"><XCircle className="w-5 h-5 text-red-600 mx-auto mb-1" /><p className="text-lg font-bold text-red-700">{csvResult.error_count}</p><p className="text-xs text-red-600">Errors</p></div>
                  </div>
                  <Button onClick={() => setShowCsvResult(false)} className="w-full bg-[#1B2A4A] text-white rounded-xl hover:bg-[#243658]" data-testid="close-csv-result">Done</Button>
                </div>
              )}
            </DialogContent>
          </Dialog>
        </TabsContent>
      </Tabs>
    </div>
  );
}
